import { mappingKinds, type MappingEntry, type MappingKind } from '../../api/types';

export type ParseFailureKey =
  | 'mappings.import.json.invalid'
  | 'mappings.import.json.noMappings'
  | 'mappings.import.json.unknownKind'
  | 'mappings.import.json.blankEntry'
  | 'mappings.import.json.duplicate';

export type ParseResult =
  { ok: true; mappings: MappingEntry[] } | { ok: false; reasonKey: ParseFailureKey; detail?: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function knownKind(value: unknown): MappingKind | null {
  return typeof value === 'string' && (mappingKinds as readonly string[]).includes(value)
    ? (value as MappingKind)
    : null;
}

/**
 * Reads what a user pastes or picks: either an export file (`{ mappings: [...] }`) or just the
 * array. Only the shape is checked here, so a typo never leaves the browser; the server decides the
 * rest — the code-list check, the tenant — and its answer is shown as it comes.
 */
export function parseMappingsJson(text: string): ParseResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, reasonKey: 'mappings.import.json.invalid' };
  }

  const raw = isRecord(parsed) ? parsed.mappings : parsed;
  if (!Array.isArray(raw) || raw.length === 0) {
    return { ok: false, reasonKey: 'mappings.import.json.noMappings' };
  }

  const mappings: MappingEntry[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!isRecord(item)) return { ok: false, reasonKey: 'mappings.import.json.blankEntry' };

    const type = knownKind(item.type);
    if (type === null) {
      return { ok: false, reasonKey: 'mappings.import.json.unknownKind', detail: String(item.type ?? '') };
    }

    const sourceValue = stringOrNull(item.sourceValue);
    const targetValue = stringOrNull(item.targetValue);
    if (sourceValue === null || targetValue === null) {
      return { ok: false, reasonKey: 'mappings.import.json.blankEntry' };
    }

    const key = `${type} ${sourceValue}`;
    if (seen.has(key)) {
      return { ok: false, reasonKey: 'mappings.import.json.duplicate', detail: `${type} ${sourceValue}` };
    }
    seen.add(key);

    mappings.push({
      type,
      sourceValue,
      targetValue,
      secondaryValue: stringOrNull(item.secondaryValue),
      description: stringOrNull(item.description),
      // Absent means enabled, the same default the API applies.
      enabled: item.enabled === undefined ? true : item.enabled === true,
    });
  }

  return { ok: true, mappings };
}

/** An export file as the console writes it — the same shape the API returns, pretty-printed. */
export function mappingsToJson(mappings: readonly MappingEntry[]): string {
  return JSON.stringify({ mappings }, null, 2);
}
